import { useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Search, Signpost } from 'lucide-react';
import { Section, Toolbar, ActionBar } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  IconButton,
  Input,
  Badge,
  Skeleton,
  SegmentedControl,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { DataTable, StatusPill, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useRedirects,
  useUpdateRedirects,
  useDeleteRedirects,
  type RedirectRuleWire,
} from '@/api/manticore';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';
import { configTone, configLabel } from '../shared';
import { CdnAspectDrift } from '../_shared/VendorDrift';

const STATUS_CODES = [
  { value: 301, label: '301 Permanent' },
  { value: 302, label: '302 Found' },
  { value: 307, label: '307 Temporary' },
  { value: 308, label: '308 Permanent' },
];
const FIELD_OPTIONS = ['url_path', 'url_query', 'request_method', 'hostname', 'user_agent', 'cookie', 'origin', 'via', 'ip_source'];
const OPERATOR_OPTIONS = [
  'equals', 'equals_ignore_case', 'not_equals', 'not_equals_ignore_case', 'regex',
  'starts_with', 'starts_with_ignore_case', 'not_starts_with', 'not_starts_with_ignore_case',
  'ends_with', 'ends_with_ignore_case', 'not_ends_with', 'not_ends_with_ignore_case',
];

interface Condition {
  id: string;
  field: string;
  operator: string;
  values: string[];
}
interface RedirectRule {
  id: string;
  name: string;
  priority: number;
  enabled: boolean;
  destination_url: string;
  status_code: number;
  conditions: Condition[];
}

let _rid = 0;
const localId = () => `rd${++_rid}`;

const hydrate = (r: RedirectRuleWire): RedirectRule => ({
  id: localId(),
  name: r.name || '',
  priority: r.priority ?? 0,
  enabled: r.enabled ?? true,
  destination_url: r.destination_url || '',
  status_code: r.status_code ?? 301,
  conditions: (r.conditions || []).map((c) => ({
    id: localId(),
    field: c.field || 'url_path',
    operator: c.operator || 'equals',
    values: Array.isArray(c.values) ? c.values : [],
  })),
});

const toWire = (r: RedirectRule): RedirectRuleWire => ({
  name: r.name.trim(),
  priority: r.priority,
  enabled: r.enabled,
  destination_url: r.destination_url.trim(),
  status_code: r.status_code,
  // Drop conditions with no values — the edge rejects a predicate whose
  // operator has nothing to match, which broke "create" for the default rule
  // (which ships one empty condition). No conditions ⇒ match all requests.
  conditions: r.conditions
    .map((c) => ({
      field: c.field,
      operator: c.operator,
      values: c.values.map((v) => v.trim()).filter(Boolean),
    }))
    .filter((c) => c.values.length > 0),
});

const blankRule = (priority: number): RedirectRule => ({
  id: localId(),
  name: '',
  priority,
  enabled: true,
  destination_url: '',
  status_code: 301,
  conditions: [{ id: localId(), field: 'url_path', operator: 'equals', values: [''] }],
});

export function RedirectsTab({ domain, domainId }: { domain: string; domainId: string }) {
  const { show } = useToast();
  const { data: cfg, isLoading } = useRedirects(domainId);
  const isConfigured = !!cfg;

  const [rules, setRules] = useState<RedirectRule[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editing, setEditing] = useState<RedirectRule | null>(null);
  const [pendingDelete, setPendingDelete] = useState<RedirectRule | null>(null);

  useEffect(() => {
    setRules((cfg?.rules ?? []).map(hydrate).sort((a, b) => a.priority - b.priority));
  }, [cfg]);

  const update = useUpdateRedirects(domainId, {
    onSuccess: () => {
      trackFeatureAction('cdn', 'redirect', isConfigured ? 'edit' : 'create');
      show({ tone: 'success', title: 'Redirects saved' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not save redirects', description: errorMessage(e, 'Could not save redirects') }),
  });
  const remove = useDeleteRedirects(domainId, {
    onSuccess: () => {
      trackFeatureAction('cdn', 'redirect', 'delete');
      show({ tone: 'success', title: 'Redirects removed' });
      setRules([]);
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not remove redirects', description: errorMessage(e, 'Could not remove redirects') }),
  });
  const busy = update.isPending || remove.isPending;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rules.filter((r) => {
      const matchesStatus = statusFilter === 'all' || String(r.status_code) === statusFilter;
      const matchesSearch =
        !q ||
        r.name.toLowerCase().includes(q) ||
        r.destination_url.toLowerCase().includes(q) ||
        r.conditions.some((c) => c.values.some((v) => v.toLowerCase().includes(q)));
      return matchesStatus && matchesSearch;
    });
  }, [rules, search, statusFilter]);

  const upsert = (rule: RedirectRule) => {
    setRules((prev) => {
      const idx = prev.findIndex((r) => r.id === rule.id);
      const next = idx > -1 ? prev.map((r) => (r.id === rule.id ? rule : r)) : [...prev, rule];
      return next.sort((a, b) => a.priority - b.priority);
    });
    setEditing(null);
  };

  const save = () => {
    for (let i = 0; i < rules.length; i++) {
      const r = rules[i];
      if (!r.name.trim()) return show({ tone: 'danger', title: `Rule #${i + 1}: name is required` });
      if (!r.destination_url.trim()) return show({ tone: 'danger', title: `Rule #${i + 1}: destination URL is required` });
    }
    update.mutate(rules.map(toWire));
  };

  const columns = useMemo<ColumnDef<RedirectRule, unknown>[]>(
    () => [
      { id: 'priority', header: 'Priority', cell: ({ row }) => <Badge tone="neutral" appearance="subtle" size="sm">{row.original.priority}</Badge> },
      { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.name || 'Untitled'}</span> },
      { id: 'status', header: 'Status', cell: ({ row }) => <Badge tone="info" appearance="subtle" size="sm">{row.original.status_code}</Badge> },
      {
        id: 'destination',
        header: 'Destination',
        cell: ({ row }) => (
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)', fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)' }}>
            {row.original.destination_url}
          </span>
        ),
      },
      {
        id: 'conditions',
        header: 'Conditions',
        cell: ({ row }) => (
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            {row.original.conditions.length
              ? `${row.original.conditions.length} condition${row.original.conditions.length !== 1 ? 's' : ''}`
              : 'All requests'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-1)' }} onClick={(e) => e.stopPropagation()}>
            <IconButton variant="ghost" size="sm" aria-label="Edit" onClick={() => setEditing(row.original)}>
              <Pencil size={15} />
            </IconButton>
            <IconButton variant="ghost" size="sm" aria-label="Delete" onClick={() => setPendingDelete(row.original)}>
              <Trash2 size={15} />
            </IconButton>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <Section
      title="Redirects"
      description={`URL redirect rules for ${domain}.`}
      actions={<StatusPill tone={configTone(isConfigured)}>{configLabel(isConfigured)}</StatusPill>}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <CdnAspectDrift domainId={domainId} path="redirects" />
        <Card>
          <CardHeader
            actions={
              <Button
                variant="secondary"
                size="sm"
                startIcon={<Plus size={14} />}
                onClick={() => setEditing(blankRule(rules.length ? Math.max(...rules.map((r) => r.priority)) + 10 : 100))}
              >
                Add rule
              </Button>
            }
          >
            <CardTitle>Rules</CardTitle>
            <CardDescription>Lower priority wins on match.</CardDescription>
          </CardHeader>
          <CardBody>
            <Toolbar
              start={
                <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Input
                    placeholder="Search redirects"
                    startSlot={<Search />}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ maxWidth: 260 }}
                  />
                  <SegmentedControl
                    size="sm"
                    items={[
                      { value: 'all', label: 'All' },
                      { value: '301', label: '301' },
                      { value: '302', label: '302' },
                      { value: '307', label: '307' },
                      { value: '308', label: '308' },
                    ]}
                    value={statusFilter}
                    onValueChange={setStatusFilter}
                    aria-label="Status filter"
                  />
                </div>
              }
            />
            {isLoading ? (
              <Skeleton style={{ height: 120 }} />
            ) : (
              <DataTable
                columns={columns}
                data={filtered}
                getRowId={(r) => r.id}
                onRowClick={(r) => setEditing(r)}
                emptyState={
                  // No rules at all → first-use; rules exist but filtered out → no-match.
                  rules.length === 0 ? (
                    <EmptyState
                      size="sm"
                      icon={<Signpost size={28} />}
                      title="No redirects"
                      description="Add a rule to redirect matching requests."
                      actions={
                        <Button size="sm" startIcon={<Plus size={14} />} onClick={() => setEditing(blankRule(100))}>
                          Add rule
                        </Button>
                      }
                    />
                  ) : (
                    <EmptyState
                      size="sm"
                      icon={<Signpost size={20} />}
                      title="No results"
                      description="No redirects match your filters."
                    />
                  )
                }
              />
            )}
          </CardBody>
        </Card>

        <ActionBar
          start={
            isConfigured ? (
              <Button variant="secondary" disabled={busy} onClick={() => remove.mutate()}>
                Remove
              </Button>
            ) : undefined
          }
          end={
            <Button disabled={busy} onClick={save}>
              {update.isPending ? 'Saving…' : isConfigured ? 'Save' : 'Create'}
            </Button>
          }
        />
      </div>

      {editing && <RedirectDialog initial={editing} onCancel={() => setEditing(null)} onSave={upsert} />}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete redirect?"
        description={`Remove ${pendingDelete?.name ? `“${pendingDelete.name}”` : 'this rule'}? Save to persist.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (pendingDelete) setRules((prev) => prev.filter((r) => r.id !== pendingDelete.id));
          setPendingDelete(null);
        }}
      />
    </Section>
  );
}

function RedirectDialog({
  initial,
  onCancel,
  onSave,
}: {
  initial: RedirectRule;
  onCancel: () => void;
  onSave: (rule: RedirectRule) => void;
}) {
  const [form, setForm] = useState<RedirectRule>(initial);
  const set = <K extends keyof RedirectRule>(k: K, v: RedirectRule[K]) => setForm((f) => ({ ...f, [k]: v }));

  const setCond = (id: string, key: keyof Condition, val: unknown) =>
    set('conditions', form.conditions.map((c) => (c.id === id ? { ...c, [key]: val } : c)));
  const addCond = () =>
    set('conditions', [...form.conditions, { id: localId(), field: 'url_path', operator: 'equals', values: [''] }]);
  const removeCond = (id: string) => set('conditions', form.conditions.filter((c) => c.id !== id));

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent style={{ maxWidth: 720 }}>
        <DialogHeader>
          <DialogTitle>{initial.name ? 'Edit redirect' : 'Add redirect'}</DialogTitle>
          <DialogDescription>Match incoming requests and redirect them to a destination.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
              <Field label="Name" required>
                {(p) => <Input {...p} value={form.name} onChange={(e) => set('name', e.target.value)} />}
              </Field>
              <Field label="Priority">
                {(p) => <Input {...p} type="number" value={String(form.priority)} onChange={(e) => set('priority', Number(e.target.value) || 0)} />}
              </Field>
              <Field label="Status code">
                {(p) => (
                  <Select value={String(form.status_code)} onValueChange={(v) => set('status_code', Number(v))}>
                    <SelectTrigger {...p}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_CODES.map((s) => (
                        <SelectItem key={s.value} value={String(s.value)}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>
            <Field label="Destination URL" required>
              {(p) => <Input {...p} value={form.destination_url} placeholder="https://example.com/new-path" onChange={(e) => set('destination_url', e.target.value)} />}
            </Field>

            <div>
              <div style={{ fontSize: 'var(--vn-font-size-md)', fontWeight: 'var(--vn-font-weight-semibold)', marginBottom: 'var(--vn-space-2)' }}>
                Conditions <span style={{ fontWeight: 'var(--vn-font-weight-regular)', color: 'var(--vn-fg-muted)' }}>(AND — all must match)</span>
              </div>
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                {form.conditions.map((c) => (
                  <div key={c.id} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'flex-start' }}>
                    <Select value={c.field} onValueChange={(v) => setCond(c.id, 'field', v)}>
                      <SelectTrigger style={{ flex: 1, minWidth: 130 }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELD_OPTIONS.map((f) => (
                          <SelectItem key={f} value={f}>
                            {f}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={c.operator} onValueChange={(v) => setCond(c.id, 'operator', v)}>
                      <SelectTrigger style={{ flex: 1, minWidth: 130 }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {OPERATOR_OPTIONS.map((o) => (
                          <SelectItem key={o} value={o}>
                            {o}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="One value per line"
                      value={c.values.join('\n')}
                      onChange={(e) => setCond(c.id, 'values', e.target.value.split('\n'))}
                      style={{ flex: 2 }}
                    />
                    <IconButton variant="ghost" size="sm" aria-label="Remove condition" disabled={form.conditions.length === 1} onClick={() => removeCond(c.id)}>
                      <Trash2 size={15} />
                    </IconButton>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 'var(--vn-space-2)' }}>
                <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={addCond}>
                  Add condition
                </Button>
              </div>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button disabled={!form.name.trim() || !form.destination_url.trim()} onClick={() => onSave(form)}>
            {initial.name ? 'Save rule' : 'Add rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
